import { createInstance, Resource } from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Static imports as fallback
import en from './locales/en.json';
import fr from './locales/fr.json';
import ar from './locales/ar.json';

const staticResources: Resource = {
  en: {
    translation: en
  },
  fr: {
    translation: fr
  },
  ar: {
    translation: ar
  }
};

// Create a fresh i18n instance
const i18n = createInstance();

// Determine API base URL - use the same base URL as authService
// Match the production URL from authService.ts
const API_BASE_URL = 'https://apidashboard.teamgreekgods.ma/api';

// Function to load translations from API
const loadTranslationsFromAPI = async (locale: string): Promise<any> => {
  try {
    const response = await fetch(`${API_BASE_URL}/translations/${locale}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to load translations: ${response.statusText}`);
    }

    const data = await response.json();
    
    // Handle backend response format: { success: true, translations: {...} }
    if (data.success && data.translations) {
      return data.translations;
    }
    
    return data.translations || data;
  } catch (error) {
    console.error(`Error loading translations for ${locale}:`, error);
    // Return static resources as fallback
    return staticResources[locale as keyof typeof staticResources]?.translation || {};
  }
};

// Deep merge API translations onto static JSON. Skips null/undefined and refuses to
// replace a nested object with an array (avoids wiping e.g. `forms` when API data is malformed).
const deepMerge = (target: any, source: any): any => {
  if (!isObject(target) || !isObject(source)) {
    return isObject(target) ? { ...target } : target;
  }
  const output: any = { ...target };
  for (const key of Object.keys(source)) {
    const srcVal = source[key];
    if (srcVal === undefined || srcVal === null) {
      continue;
    }
    const tgtVal = output[key];
    if (Array.isArray(srcVal)) {
      if (isObject(tgtVal) && !Array.isArray(tgtVal)) {
        continue;
      }
      output[key] = srcVal;
      continue;
    }
    if (isObject(srcVal)) {
      if (isObject(tgtVal) && !Array.isArray(tgtVal)) {
        output[key] = deepMerge(tgtVal, srcVal);
      } else if (tgtVal === undefined) {
        output[key] = { ...srcVal };
      } else {
        output[key] = deepMerge({}, srcVal);
      }
      continue;
    }
    output[key] = srcVal;
  }
  return output;
};

const isObject = (item: any): boolean => {
  return item && typeof item === 'object' && !Array.isArray(item);
};

// Load all translations from API and update i18n resources
const loadAllTranslationsFromAPI = async () => {
  try {
    const locales = ['en', 'fr', 'ar'];
    const loadedResources: Resource = {};

    // Load all translations in parallel
    const translations = await Promise.all(
      locales.map(async (locale) => {
        const apiTranslation = await loadTranslationsFromAPI(locale);
        const staticTranslation = staticResources[locale as keyof typeof staticResources]?.translation || {};
        // Merge API translations with static translations, keeping static as base
        const mergedTranslation = deepMerge(staticTranslation, apiTranslation);
        return { locale, translation: mergedTranslation };
      })
    );

    // Build resources object
    translations.forEach(({ locale, translation }) => {
      loadedResources[locale] = {
        translation: translation
      };
    });

    // Update i18n resources with merged translations (deep merge, overwrite to update existing keys)
    Object.keys(loadedResources).forEach((locale) => {
      i18n.addResourceBundle(locale, 'translation', loadedResources[locale].translation, true, true);
    });
  } catch (error) {
    console.error('Error loading all translations from API:', error);
  }
};

// Initialize i18n with static resources first (for immediate use)
i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: staticResources,
    fallbackLng: 'en',
    debug: false,
    interpolation: {
      escapeValue: false
    },
    react: {
      useSuspense: false
    },
    initImmediate: false // Ensure synchronous initialization
  })
  .then(() => {
    // After initialization, load translations from API and update resources
    loadAllTranslationsFromAPI();
  });

// Export function to reload translations (useful when translations are updated)
export const reloadTranslations = async () => {
  await loadAllTranslationsFromAPI();
  // Force a re-render by changing language to current language
  const currentLang = i18n.language;
  await i18n.changeLanguage(currentLang);
};

export default i18n;